import { Fragment, useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Type } from 'lucide-react';
import { BlockCard } from './BlockCard';
import { BlockPalette } from './BlockPalette';
import {
  BLOCCHI_DISPONIBILI,
  bloccoNuovo,
  tipoDaIdPalette,
} from './blocchiDisponibili';
import { cn } from '../../lib/cn';
import type { Blocco, DocumentoEmail, TipoBlocco } from '@email/types.ts';

interface BlockEditorProps {
  documento: DocumentoEmail;
  onChange: (documento: DocumentoEmail) => void;
}

/** Identificativi delle zone in cui si lascia cadere un blocco nuovo. */
const PREFISSO_ZONA = 'zona:';
const ID_TELA = 'tela';

function indiceDaIdZona(id: string): number | null {
  if (!id.startsWith(PREFISSO_ZONA)) return null;
  const n = Number(id.slice(PREFISSO_ZONA.length));
  return Number.isInteger(n) ? n : null;
}

/**
 * Lo spazio fra due blocchi. Normalmente e' solo aria; mentre si trascina un
 * blocco nuovo diventa un bersaglio che si illumina.
 */
function ZonaRilascio({ indice, attiva }: { indice: number; attiva: boolean }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `${PREFISSO_ZONA}${indice}`,
    disabled: !attiva,
  });

  return (
    <div ref={setNodeRef} className={cn('transition-all', attiva ? 'h-10 py-1' : 'h-2')}>
      {attiva && (
        <div
          className={cn(
            'h-full rounded-md border-2 border-dashed flex items-center justify-center text-[11px] font-medium transition-colors',
            isOver
              ? 'border-primary-500 bg-primary-50 text-primary-600'
              : 'border-gray-200 text-neutral-300'
          )}
        >
          {isOver ? 'Lascia qui' : ''}
        </div>
      )}
    </div>
  );
}

/** La tela vuota: quando non c'e' ancora nessun blocco, il bersaglio e' tutta lei. */
function TelaVuota({ attiva }: { attiva: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: ID_TELA });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'text-center py-12 rounded-xl border border-dashed bg-white transition-colors',
        isOver && attiva ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
      )}
    >
      <Type className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
      <p className="text-sm text-neutral-500">
        {isOver && attiva ? 'Lascia qui il blocco' : 'La email e\' vuota'}
      </p>
      <p className="text-xs text-neutral-400 mt-1 px-4">
        Trascina un blocco dal pannello qui accanto. L'intestazione con il logo e il piede con i
        dati aziendali ci sono gia'.
      </p>
    </div>
  );
}

/**
 * La tela su cui si compone la email.
 *
 * Due movimenti, la stessa libreria (@dnd-kit, quella della lavagna dei lead):
 * dal pannello si trascina un blocco nuovo nel punto voluto, e i blocchi gia'
 * dentro si riordinano trascinandoli su e giu'.
 */
export function BlockEditor({ documento, onChange }: BlockEditorProps) {
  // Col mouse basta un piccolo spostamento; col dito serve una pressione
  // prolungata, altrimenti la pagina non si riesce piu' a scorrere.
  const sensori = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  // Che tipo di blocco si sta trascinando dal pannello. Null quando si sta
  // riordinando un blocco gia' dentro la email, o quando non si trascina.
  const [inArrivo, setInArrivo] = useState<TipoBlocco | null>(null);

  const ids = useMemo(() => documento.blocchi.map((b) => b.id), [documento.blocchi]);

  const inserisci = useCallback(
    (tipo: TipoBlocco, posizione: number) => {
      const blocchi = [...documento.blocchi];
      blocchi.splice(posizione, 0, bloccoNuovo(tipo));
      onChange({ ...documento, blocchi });
    },
    [documento, onChange]
  );

  const aggiungiInFondo = useCallback(
    (tipo: TipoBlocco) => inserisci(tipo, documento.blocchi.length),
    [documento.blocchi.length, inserisci]
  );

  const modifica = useCallback(
    (blocco: Blocco) => {
      onChange({
        ...documento,
        blocchi: documento.blocchi.map((b) => (b.id === blocco.id ? blocco : b)),
      });
    },
    [documento, onChange]
  );

  const duplica = useCallback(
    (id: string) => {
      const indice = documento.blocchi.findIndex((b) => b.id === id);
      if (indice < 0) return;
      const copia = { ...documento.blocchi[indice], id: crypto.randomUUID() } as Blocco;
      const blocchi = [...documento.blocchi];
      blocchi.splice(indice + 1, 0, copia);
      onChange({ ...documento, blocchi });
    },
    [documento, onChange]
  );

  const elimina = useCallback(
    (id: string) => {
      onChange({ ...documento, blocchi: documento.blocchi.filter((b) => b.id !== id) });
    },
    [documento, onChange]
  );

  const iniziato = useCallback((evento: DragStartEvent) => {
    setInArrivo(tipoDaIdPalette(String(evento.active.id)));
  }, []);

  // Trascinando dal pannello il bersaglio e' lo spazio *fra* i blocchi, non il
  // blocco: fra tutte le collisioni si tengono le zone, e si ricade sul blocco
  // sottostante solo se il puntatore non e' su nessuna.
  const rilevamento: CollisionDetection = useCallback(
    (args) => {
      if (!inArrivo) return closestCenter(args);
      const collisioni = pointerWithin(args);
      const utili = collisioni.length > 0 ? collisioni : rectIntersection(args);
      const zone = utili.filter(
        (c) => String(c.id).startsWith(PREFISSO_ZONA) || c.id === ID_TELA
      );
      return zone.length > 0 ? zone : utili;
    },
    [inArrivo]
  );

  const finito = useCallback(
    (evento: DragEndEvent) => {
      const { active, over } = evento;
      const tipo = tipoDaIdPalette(String(active.id));
      setInArrivo(null);

      if (!over) return;

      // Blocco nuovo che arriva dal pannello.
      if (tipo) {
        if (over.id === ID_TELA) {
          inserisci(tipo, documento.blocchi.length);
          return;
        }
        const zona = indiceDaIdZona(String(over.id));
        if (zona !== null) {
          inserisci(tipo, zona);
          return;
        }
        // Lasciato sopra un blocco: entra al suo posto, quello scende.
        const sopra = ids.indexOf(String(over.id));
        inserisci(tipo, sopra < 0 ? documento.blocchi.length : sopra);
        return;
      }

      // Riordino di un blocco gia' dentro la email.
      if (active.id === over.id) return;
      const da = ids.indexOf(String(active.id));
      const a = ids.indexOf(String(over.id));
      if (da < 0 || a < 0) return;
      onChange({ ...documento, blocchi: arrayMove(documento.blocchi, da, a) });
    },
    [documento, ids, inserisci, onChange]
  );

  const voceInArrivo = BLOCCHI_DISPONIBILI.find((v) => v.tipo === inArrivo);

  return (
    <DndContext
      sensors={sensori}
      collisionDetection={rilevamento}
      onDragStart={iniziato}
      onDragEnd={finito}
      onDragCancel={() => setInArrivo(null)}
    >
      <div className="grid gap-4 lg:grid-cols-[200px_minmax(0,1fr)]">
        <BlockPalette onAggiungi={aggiungiInFondo} />

        <div>
          {documento.blocchi.length === 0 ? (
            <TelaVuota attiva={inArrivo !== null} />
          ) : (
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div>
                <ZonaRilascio indice={0} attiva={inArrivo !== null} />
                {documento.blocchi.map((blocco, i) => (
                  <Fragment key={blocco.id}>
                    <BlockCard
                      blocco={blocco}
                      onChange={modifica}
                      onDuplica={() => duplica(blocco.id)}
                      onElimina={() => elimina(blocco.id)}
                    />
                    <ZonaRilascio indice={i + 1} attiva={inArrivo !== null} />
                  </Fragment>
                ))}
              </div>
            </SortableContext>
          )}
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {voceInArrivo ? (
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-primary-400 bg-white shadow-lg">
            <voceInArrivo.icona className="w-3.5 h-3.5 text-primary-600" />
            <span className="text-xs font-semibold text-gray-900">{voceInArrivo.etichetta}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
